import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, InputGroup, Button, Table, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { COLORS, API_URL } from "../../types/profession.types";
import {
  getAvailableCities,
  getAllCityPrices,
  upsertCityPrice,
  deactivateCity,
  formatCityPrice,
  getPricingModelLabel,
} from "../../../../services/professionService";

export const CityPricingTab: React.FC = () => {
  const [cities, setCities] = useState<any[]>([]);
  const [cityConfigs, setCityConfigs] = useState<Record<string, any>>({});
  const [cityStats, setCityStats] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [editingCity, setEditingCity] = useState<string | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCities, setTotalCities] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [editForm, setEditForm] = useState({
    city_name: "",
    pricing_model: "fixed" as "fixed" | "per_record",
    fixed_price: null as number | null,
    price_per_record: null as number | null,
    is_active: true
  });

  const loadData = async (page = currentPage) => {
    try {
      setLoading(true);
      
      const [availableCities, prices, statsResponse] = await Promise.all([
        getAvailableCities(),
        getAllCityPrices(),
        axios.get(`${API_URL}/profession/city-stats?page=${page}&limit=${pageSize}&search=${searchText}`)
      ]);
      
      if (statsResponse.data.success) {
        setCityStats(statsResponse.data.data);
        setTotalPages(statsResponse.data.pagination.totalPages);
        setTotalCities(statsResponse.data.pagination.total);
        setCurrentPage(statsResponse.data.pagination.page);
      }
      
      setCities(availableCities);
      
      const configMap: Record<string, any> = {};
      prices.forEach(price => {
        configMap[price.city_name] = price;
      });
      setCityConfigs(configMap);
      
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(currentPage);
  }, [currentPage, searchText, pageSize]);

  const handleEdit = (cityName: string) => {
    const config = cityConfigs[cityName];
    
    setEditingCity(cityName);
    setEditForm({
      city_name: cityName,
      pricing_model: config?.pricing_model || 'fixed',
      fixed_price: config?.fixed_price || null,
      price_per_record: config?.price_per_record || null,
      is_active: config?.is_active ?? true
    });
  };

  const handleSave = async () => {
    if (!editingCity) return;
    
    try {
      setLoading(true);
      await upsertCityPrice(editingCity, editForm);
      toast.success(`Configuration enregistrée pour ${editingCity}`);
      await loadData(currentPage);
      setEditingCity(null);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (cityName: string) => {
    if (!window.confirm(`Désactiver l'export pour ${cityName} ?`)) return;
    
    try {
      setLoading(true);
      await deactivateCity(cityName);
      toast.success(`${cityName} désactivée`);
      await loadData(currentPage);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Barre de recherche */}
      <Card className="mb-4 shadow-sm" style={{ borderRadius: "12px", border: `1px solid ${COLORS.border}` }}>
        <Card.Body className="p-4">
          <Row className="g-3 align-items-center">
            <Col xs={12} md={5}>
              <InputGroup>
                <InputGroup.Text style={{ backgroundColor: COLORS.light, borderColor: COLORS.border }}>
                  🔍
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Rechercher une ville..."
                  value={searchText}
                  onChange={handleSearch}
                  style={{ borderColor: COLORS.border, height: "44px" }}
                />
                {searchText && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setSearchText("");
                      setCurrentPage(1);
                    }}
                    style={{ borderColor: COLORS.border, height: "44px" }}
                  >
                    ✕
                  </Button>
                )}
              </InputGroup>
            </Col>
            
            {/* <Col xs={6} md={3}>
              <Form.Select 
                value={pageSize} 
                onChange={handlePageSizeChange}
                style={{ height: "44px", borderColor: COLORS.border }}
              >
                <option value={25}>25 par page</option>
                <option value={50}>50 par page</option>
                <option value={100}>100 par page</option>
                <option value={200}>200 par page</option>
              </Form.Select>
            </Col> */}
          </Row>
        </Card.Body>
      </Card>

      {/* Tableau */}
      <div 
        className="table-responsive"
        style={{ 
          maxHeight: "calc(100vh - 350px)", 
          overflowY: "auto",
          overflowX: "auto",
          borderRadius: "12px",
          border: `1px solid ${COLORS.border}`,
          backgroundColor: "white"
        }}
      >
        <Table striped bordered hover className="align-middle mb-0" style={{ minWidth: "1200px" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 102, backgroundColor: "white" }}>
            <tr>
              {["Ville", "Statut", "Modèle", "Prix", "Contacts", "Actions"].map((header, idx) => (
                <th 
                  key={idx}
                  style={{ 
                    position: "sticky", 
                    top: 0, 
                    backgroundColor: COLORS.primary, 
                    color: "white",
                    padding: "16px 12px",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    minWidth: header === "Ville" ? "200px" : 
                             header === "Actions" ? "150px" : 
                             header === "Prix" ? "150px" : 
                             header === "Contacts" ? "150px" : "100px",
                    borderBottom: "none",
                    justifyContent: "center",
                    textAlign:"center"
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-5">
                  <Spinner animation="border" style={{ color: COLORS.primary }} />
                  <p className="mt-3" style={{ color: COLORS.textLight }}>Chargement des villes...</p>
                </td>
              </tr>
            ) : Object.keys(cityStats).length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-5">
                  <div style={{ color: COLORS.textLight }}>
                    <span style={{ fontSize: "3rem", display: "block", marginBottom: "1rem" }}>🏙️</span>
                    <h5>Aucune ville trouvée</h5>
                    <p className="mb-0">Essayez de modifier vos critères de recherche</p>
                  </div>
                </td>
              </tr>
            ) : (
              Object.entries(cityStats).map(([cityName, contactCount], index) => {
                const config = cityConfigs[cityName];
                const isEditing = editingCity === cityName;
                
                return (
                  <tr 
                    key={cityName}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#ffffff" : COLORS.light,
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e6f7ff"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : COLORS.light}
                  >
                    <td className="fw-bold" style={{ padding: "14px 12px" }}>{cityName}</td>
                    
                    <td style={{ padding: "14px 12px" ,justifyContent: "center",textAlign:"center"}}>
                      {isEditing ? (
                        <Form.Check
                          type="switch"
                          checked={editForm.is_active}
                          onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                        />
                      ) : (
                        <span 
                          className="badge"
                          style={{
                            backgroundColor: config?.is_active ? COLORS.success : COLORS.textLight,
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "20px",
                            fontSize: "0.85rem"
                          }}
                        >
                          {config?.is_active ? 'PAYANT' : 'GRATUIT'}
                        </span>
                      )}
                    </td>
                    
                    <td style={{ padding: "14px 12px" }}>
                      {isEditing ? (
                        <Form.Select
                          value={editForm.pricing_model}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            pricing_model: e.target.value as "fixed" | "per_record",
                            fixed_price: null,
                            price_per_record: null
                          })}
                          size="sm"
                        >
                          <option value="fixed">Prix fixe</option>
                          <option value="per_record">Prix par contact</option>
                        </Form.Select>
                      ) : (
                        config && (
                          <span 
                            className="badge"
                            style={{
                              backgroundColor: COLORS.info,
                              color: "white",
                              padding: "6px 12px",
                              borderRadius: "20px",
                              fontSize: "0.85rem"
                            }}
                          >
                            {getPricingModelLabel(config.pricing_model)}
                          </span>
                        )
                      )}
                    </td>
                    
                    <td style={{ padding: "14px 12px",justifyContent: "center",textAlign:"center" }}>
                      {isEditing ? (
                        editForm.pricing_model === 'fixed' ? (
                          <InputGroup size="sm">
                            <Form.Control
                              type="number"
                              min="0"
                              step="1"
                              value={editForm.fixed_price || ''}
                              onChange={(e) => setEditForm({ ...editForm, fixed_price: e.target.value ? Number(e.target.value) : null })}
                              placeholder="Prix fixe"
                              style={{ maxWidth: "120px" }}
                            />
                            <InputGroup.Text>€</InputGroup.Text>
                          </InputGroup>
                        ) : (
                          <InputGroup size="sm">
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={editForm.price_per_record || ''}
                              onChange={(e) => setEditForm({ ...editForm, price_per_record: e.target.value ? Number(e.target.value) : null })}
                              placeholder="Prix/contact"
                              style={{ maxWidth: "120px" }}
                            />
                            <InputGroup.Text>€</InputGroup.Text>
                          </InputGroup>
                        )
                      ) : (
                        <span style={{ fontWeight: 500 }}>
                          {config ?  formatCityPrice(config) : (
                            <span style={{ color: COLORS.textLight }}>Non configuré</span>
                          )}
                        </span>
                      )}
                    </td>
                    
                    <td style={{ padding: "14px 12px" ,justifyContent: "center",textAlign:"center"}}>
                      <div>
                        <span 
                          className="badge"
                          style={{
                            backgroundColor: COLORS.primary,
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "20px",
                            fontSize: "0.85rem"
                          }}
                        >
                          {contactCount} contact{contactCount !== 1 ? 's' : ''}
                        </span>
                        {contactCount > 0 && config?.pricing_model === 'per_record' && config.price_per_record && (
                          <div className="small text-muted mt-2">
                            Total: <strong>{(contactCount * config.price_per_record).toFixed(2)} €</strong>
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td style={{ padding: "14px 12px" }}>
                      {isEditing ? (
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="success"
                            onClick={handleSave}
                            style={{ minWidth: "40px", height: "38px" }}
                          >
                            ✓
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setEditingCity(null)}
                            style={{ minWidth: "40px", height: "38px" }}
                          >
                            ✗
                          </Button>
                        </div>
                      ) : (
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleEdit(cityName)}
                            title="Modifier"
                            style={{ 
                              borderColor: COLORS.primary,
                              color: COLORS.primary,
                              minWidth: "40px",
                              height: "38px"
                            }}
                          >
                            ✏️
                          </Button>
                          {config?.is_active && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeactivate(cityName)}
                              title="Désactiver"
                              style={{ 
                                borderColor: COLORS.danger,
                                color: COLORS.danger,
                                minWidth: "40px",
                                height: "38px"
                              }}
                            >
                              🚫
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-4 gap-3">
          <div className="text-muted small">
            <span style={{ backgroundColor: COLORS.light, padding: "8px 16px", borderRadius: "20px" }}>
              Page {currentPage} sur {totalPages} • {totalCities} villes
            </span>
          </div>
          
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              style={{ borderColor: COLORS.border, color: COLORS.text, padding: "8px 20px", minWidth: "100px" }}
            >
              ← Précédent
            </Button>
            
            <Button
              variant="outline-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
              style={{ borderColor: COLORS.border, color: COLORS.text, padding: "8px 20px", minWidth: "100px" }}
            >
              Suivant →
            </Button>
          </div>
        </div>
      )}

      {/* Message d'aide pour mobile */}
      <div className="d-block d-sm-none mt-3 p-3 text-center" style={{ backgroundColor: COLORS.light, borderRadius: "12px" }}>
        <small className="text-muted">
          <span style={{ marginRight: '8px', fontSize: '1.2rem' }}>📱</span>
          Faites défiler horizontalement pour voir toutes les colonnes
        </small>
      </div>

      {/* Légende */}
      <Card className="mt-4" style={{ border: `1px solid ${COLORS.border}`, borderRadius: "12px", backgroundColor: COLORS.light }}>
        <Card.Body className="p-4">
          <Row className="g-4">
            <Col xs={12} md={4}>
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: "1.5rem" }}>💡</span>
                <div>
                  <small className="text-muted d-block">Villes</small>
                  <span>Détectées automatiquement depuis l'annuaire</span>
                </div>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: "1.5rem" }}>💰</span>
                <div>
                  <small className="text-muted d-block">Modèles</small>
                  <span>Prix fixe ou par contact</span>
                </div>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: "1.5rem" }}>🔄</span>
                <div>
                  <small className="text-muted d-block">Pagination</small>
                  <span>{pageSize} villes par page</span>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};